import type { DateOnly, MonthOnly } from "@shared/api"

/**
 * Formateo compartido entre las dos vistas del tablero. Vive en un módulo aparte
 * para que RequestsPage no re-derive la trampa de UTC de monthLabel: todo lo que
 * formatea fechas de CALENDARIO (MonthOnly, work_date) lleva timeZone UTC
 * obligatorio — sin eso, el 1 de agosto a las 00:00 UTC se formatea como "julio"
 * en cualquier offset negativo, y la firma está en Nueva York.
 */

export const MONTHS_IN_PICKER = 24

/**
 * Los últimos 24 meses, del más nuevo al más viejo.
 *
 * NO se consulta la base para armar esta lista: un mes sin datos simplemente
 * muestra el estado vacío. Eso ahorra una vista SQL y, sobre todo, evita traer
 * todas las filas históricas solo para poblar un <select>.
 *
 * Todo en UTC (Date.UTC + el mismo mes que devuelve currentMonthUtc en
 * src/api.ts): el "mes actual" tiene que ser el mismo del lado del servidor.
 */
export function recentMonths(now: Date = new Date()): MonthOnly[] {
  const out: MonthOnly[] = []
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth() // 0-11
  for (let i = 0; i < MONTHS_IN_PICKER; i++) {
    const d = new Date(Date.UTC(y, m - i, 1))
    out.push(d.toISOString().slice(0, 7))
  }
  return out
}

const MONTH_LABEL = new Intl.DateTimeFormat("es", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
})

/** 'YYYY-MM' → 'agosto de 2026'. */
export function monthLabel(month: MonthOnly): string {
  const [y, m] = month.split("-").map(Number)
  return MONTH_LABEL.format(new Date(Date.UTC(y, m - 1, 1)))
}

const DATE_LABEL = new Intl.DateTimeFormat("es", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
})

/** 'YYYY-MM-DD' (un `date` pelado, como work_date) → '19/08/2026'. */
export function formatDate(d: DateOnly): string {
  const [y, m, day] = d.split("-").map(Number)
  return DATE_LABEL.format(new Date(Date.UTC(y, m - 1, day)))
}

/**
 * Un instante ISO (requested_at, decided_at) → fecha corta en zona LOCAL: acá sí
 * es un timestamp de verdad, no un `date` pelado — misma lógica que el
 * `generated_at` de la línea de frescura.
 */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString("es", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

/**
 * Horas con 2 decimales FIJOS.
 *
 * Divergencia deliberada con el formatHours de src/parse.ts, que recorta los
 * ceros ("7.5") porque va en prosa dentro de un mensaje de Slack. Acá es una
 * columna que se lee de arriba abajo para pagar: los decimales fijos, más el
 * tabular-nums de .numeric, son lo que hace que los números no bailen entre filas.
 */
export function formatHours(h: number): string {
  return h.toFixed(2)
}

/** Sin departamento cargado (filas viejas, o el campo se dejó vacío). */
export const NO_DEPARTMENT = "—"
